import {
  field,
  getDrugSubmissionDisplayModel,
} from "../../helpers";
import { fdaDrugsTheme } from "../themes";

const REMS_STATUS_LABELS = {
  none: "None required",
  active: "Active REMS exists",
  discontinued: "Discontinued REMS",
};

const formatRemsStatus = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return REMS_STATUS_LABELS[normalized] || REMS_STATUS_LABELS.none;
};

const buildDrugsFdaApprovalHref = (applicationNumber) => {
  const applicationDigits = String(applicationNumber || "")
    .trim()
    .replace(/^(NDA|ANDA|BLA)\s*/i, "")
    .replace(/\D/g, "");

  return applicationDigits
    ? `https://www.accessdata.fda.gov/scripts/cder/daf/index.cfm?event=overview.process&ApplNo=${applicationDigits}`
    : "";
};

const parseApplicationNumber = (applicationNumber) => {
  const normalized = String(applicationNumber || "").trim();
  const match = normalized.match(/^(ANDA|NDA|BLA)\s*(.*)$/i);

  if (!match) {
    return { type: "", digits: "" };
  }

  return {
    type: match[1].toUpperCase(),
    digits: match[2].replace(/\D/g, ""),
  };
};

const buildOrangeBookHref = (applicationNumber) => {
  const { type, digits } = parseApplicationNumber(applicationNumber);
  const applType = type === "ANDA" ? "A" : type === "NDA" ? "N" : "";

  return applType && digits
    ? `https://www.accessdata.fda.gov/scripts/cder/ob/results_product.cfm?Appl_Type=${applType}&Appl_No=${digits}`
    : "";
};

const buildPurpleBookHref = (applicationNumber) => {
  const { type, digits } = parseApplicationNumber(applicationNumber);

  return type === "BLA" && digits
    ? `https://purplebooksearch.fda.gov/index.cfm?blaNo=${digits}&event=productdetails`
    : "";
};

const normalizeStringList = (value) => {
  const values = Array.isArray(value) ? value : [value];

  return values
    .map((item) => String(item || "").trim())
    .filter(Boolean);
};

const isHttpUrl = (value) => /^https?:\/\//i.test(String(value || "").trim());

const buildDailyMedHref = (splSetId) => {
  const normalized = String(splSetId || "").trim();

  return normalized
    ? `https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=${normalized}`
    : "";
};

const buildSplSetIdLinks = (record, value) =>
  normalizeStringList(value).map((splSetId) => ({
    text: splSetId,
    href: buildDailyMedHref(splSetId),
  }));

const buildDailyMedLinks = (record, value) => {
  const storedLinks = normalizeStringList(value || record?.dailymed_links).filter(isHttpUrl);

  if (storedLinks.length) {
    return storedLinks.map((href, index) => ({
      text: normalizeStringList(record?.spl_set_id)[index] || `SPL Set ID ${index + 1}`,
      href,
    }));
  }

  return buildSplSetIdLinks(record, record?.spl_set_id);
};

const fdaDrugsConfig = {
  label: "FDA Drugs",
  theme: fdaDrugsTheme,
  summary: {
    primary: field("Brand name", [ "generic_name"]),
    secondary: field("Generic Name", ["generic_name"]),
    columns: [
      field("Sponsor", ["sponsor_name"], {
        className: "hidden lg:block flex-[1.25] min-w-[9rem]",
      }),
      field("Application", ["application_number"], {
        className: "hidden md:block flex-[0.95] min-w-[7rem]",
        kind: "id",
      }),
      field("Approval Date", ["effective_time"], {
        className: "hidden xl:block flex-[0.9] min-w-[7rem]",
      }),
      field("Type", ["latest_submission_type"], {
        className: "flex-[0.8] min-w-[6rem]",
        kind: "accent",
      }),
    ],
  },
  detail: {
    meta: [
      field("Application", ["application_number"], { kind: "id" }),
      field("Approval Date", ["effective_time"], {
        className: "hidden xl:block flex-[0.9] min-w-[7rem]",
      }),
      field("Status", ["marketing_status"], { kind: "accent" }),
    ],
    sections: [
      {
        title: "Sponsor",
        fields: [field("Sponsor name", ["sponsor_name"])],
      },
      {
        title: "Drug",
        fields: [
          field("Generic name", ["generic_name"]),
          field("Type", ["latest_submission_type"], {
            className: "flex-[0.8] min-w-[6rem]",
            kind: "accent",
          }),
          field("Dosage form", ["dosage_forms"]),
          field("Route", ["route"]),
          field("Active Ingredient", ["products.active_ingredients.name"]),
          field("Strength", ["products.active_ingredients.strength"]),
        ],
      },
      {
        title: "Submissions",
        fields: [
          field("Drug submissions", [
            "original_submission",
            "supplemental_submissions",
            "submissions",
          ], {
            kind: "drug-submissions",
            formatValue: getDrugSubmissionDisplayModel,
          }),
        ],
      },
      {
        title: "Indication",
        fields: [field("Approved Indication", ["indications_and_usage"])],
      },
      {
        title: "Regulatory Designations",
        fields: [
          field("Priority review", ["latest_submission.review_priority"]),
          field("Breakthrough therapy", ["breakthrough_therapy"]),
          field("Orphan drug", ["orphan_drug"]),
          field("Accelerated approval", ["accelerated_approval"]),
          field("TE code", ["te_code"]),
        ],
      },
      {
        title: "Public documents",
        fields: [
          field("REMS status", ["rems_status"], {
            includeMissing: true,
            missingValue: REMS_STATUS_LABELS.none,
            formatValue: formatRemsStatus,
          }),
          field("REMS link", ["rems_url"], {
            kind: "link",
            includeMissing: true,
            missingValue: "Not available",
            buildHref: (record, value) => value || "",
          }),
        ],
      },
      {
        title: "Other Identifiers",
        fields: [
          field("UNII", ["unii"], {
            includeMissing: true,
            missingValue: "Not available",
          }),
          field("SPL Set ID", ["spl_set_id"], {
            includeMissing: true,
            missingValue: "Not available",
            kind: "multi-link",
            buildLinks: buildSplSetIdLinks,
          }),
          field("Pharmacological Class epc", ["pharm_class_epc"], {
            includeMissing: true,
            missingValue: "Not available",
          }), 
            field("Pharmacological Class MoA", ["pharm_class_moa"], {
            includeMissing: true,
            missingValue: "Not available",
          }), 
        ],
      },
    ],
    links: [
      {
        label: "drugs@FDA approval package",
        buildHref: (record, value) => buildDrugsFdaApprovalHref(value),
        sources: ["application_number"],
        valueLabel: "Open approval package",
      },
      {
        label: "Orange/Purple Book",
        buildHref: (record, value) =>
          (isHttpUrl(value) ? value : "") ||
          buildOrangeBookHref(record?.application_number) ||
          buildPurpleBookHref(record?.application_number),
        sources: ["orange_book_link", "purple_book_link", "application_number"],
        valueLabel: "Patent & exclusivity listing",
      },
      {
        label: "DailyMed Link",
        buildLinks: buildDailyMedLinks,
        sources: ["dailymed_links", "dailymed_link", "spl_set_id"],
      },
    ],
  },
};

export default fdaDrugsConfig;
